import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Filter,
  Truck,
  Clock,
  Download,
  FileText,
  FileSpreadsheet,
  X,
  Search,
  History,
  UserCheck,
} from "lucide-react";
import TrackingModal from "./TrackingModal";
// AlterCPA removed for Greek-CC
// import { useAppSelector } from "../../store/hooks";
// import { getAccountById } from "../../store/slices/altercpaSlice";
import { API_BASE_URL } from "../../utils/apiConfig";

interface ACSTrackingStatus {
  status: "Transit" | "Delivered" | "Returned";
  isComplicated: boolean;
  reason: string | null;
  deliveryFlag: number;
  returnedFlag: number;
  shipmentStatus: number | null;
  deliveryInfo: string | null;
  lastCheckedAt: string | { $date: string };
}

interface OrderWithTracking {
  id: number;
  trackingId: string;
  shippingCompany?: string;
  acsTrackingStatus?: ACSTrackingStatus;
  time: number;
  name?: string;
  phone?: string;
  country?: string;
  packingTimestamp?: number | { $date: string };
  altercpaPackingStateTime?: string | { $date: string };
  lastUpdated?: string | { $date: string };
  shippingDate?: string | { $date: string };
  postOfficeSelectedAt?: string | { $date: string };
  source?: string;
  createdBy?: {
    username?: string;
    role?: string;
  };
  user?: number;
  accountInfo?: {
    id?: number | null;
    username?: string | null;
    email?: string | null;
    level?: string;
    team?: string | null;
  };
}

interface DeliveryStats {
  Transit: number;
  Delivered: number;
  Returned: number;
  Complicated: number;
}

interface DeliveredStatisticsProps {
  currentUser?: any;
}

const DeliveredStatistics: React.FC<DeliveredStatisticsProps> = ({
  currentUser,
}) => {
  // AlterCPA removed for Greek-CC
  // const altercpaState = useAppSelector((state) => state.altercpa);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState("this_month");
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [stats, setStats] = useState<DeliveryStats>({
    Transit: 0,
    Delivered: 0,
    Returned: 0,
    Complicated: 0,
  });
  const [orders, setOrders] = useState<OrderWithTracking[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [complicationFilter, setComplicationFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sortBy, setSortBy] = useState<string>("shippingDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [timePeriodGroupBy, setTimePeriodGroupBy] = useState<string>("shippingDate");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState<OrderWithTracking | null>(null);
  const [trackingData, setTrackingData] = useState<any>(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);

  const apiBaseUrl = API_BASE_URL;

  // Helper function to parse MongoDB date objects or regular dates
  const parseDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    // Handle MongoDB date objects like {"$date": "2025-10-16T10:12:15.370Z"}
    if (typeof dateValue === 'object' && dateValue.$date) {
      return new Date(dateValue.$date);
    }
    
    // Handle regular date strings or numbers
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    
    if (typeof dateValue === 'number') {
      // Handle Unix timestamps (convert to milliseconds if needed)
      return new Date(dateValue < 10000000000 ? dateValue * 1000 : dateValue);
    }
    
    return null;
  };

  // Helper function to get the best available timestamp for approval time (same logic as CountrySection.tsx)
  const getBestTimestamp = (order: OrderWithTracking): number | null => {
    // Priority order: packingTimestamp > altercpaPackingStateTime > lastUpdated
    
    // Check packingTimestamp first (can be Unix timestamp or MongoDB date object)
    if (order.packingTimestamp) {
      const date = parseDate(order.packingTimestamp);
      if (date && !isNaN(date.getTime())) {
        return date.getTime();
      }
    }
    
    // Check altercpaPackingStateTime (ISO date string or MongoDB date object)
    if (order.altercpaPackingStateTime) {
      const date = parseDate(order.altercpaPackingStateTime);
      if (date && !isNaN(date.getTime())) {
        return date.getTime();
      }
    }
    
    // Check lastUpdated (ISO date string or MongoDB date object)
    if (order.lastUpdated) {
      const date = parseDate(order.lastUpdated);
      if (date && !isNaN(date.getTime())) {
        return date.getTime();
      }
    }
    
    return null;
  };

  // Helper function to get shipping date (prioritize shippingDate, then postOfficeSelectedAt)
  const getShippingDate = (order: OrderWithTracking): string | null => {
    // Check shippingDate first (can be string or MongoDB date object)
    if (order.shippingDate) {
      const date = parseDate(order.shippingDate);
      if (date && !isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    
    // Check postOfficeSelectedAt as fallback
    if (order.postOfficeSelectedAt) {
      const date = parseDate(order.postOfficeSelectedAt);
      if (date && !isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    
    return null;
  };

  const fetchDeliveredStats = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (dateRange === "custom") {
        params.append("customStartDate", customDateRange.start);
        params.append("customEndDate", customDateRange.end);
        params.append("dateRange", "custom");
      } else {
        params.append("dateRange", dateRange);
      }
      
      // Add the 'by' parameter to specify which date field to sort by
      params.append("by", timePeriodGroupBy);

      // Add pagination
      params.append("page", currentPage.toString());
      params.append("limit", itemsPerPage.toString());

      // Add sorting (if sortBy is set)
      if (sortBy && sortOrder) {
        params.append("sortBy", sortBy);
        params.append("sortOrder", sortOrder);
      }

      // Add search filter
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      // Add status filter
      if (statusFilter !== "all") {
        params.append("statusFilter", statusFilter);
      }

      // Add complication filter
      if (complicationFilter !== "all") {
        params.append("complicationFilter", complicationFilter);
      }

      const response = await fetch(
        `${apiBaseUrl}/api/shipping/acs/delivered-stats?${params.toString()}`
      );
      const result = await response.json();

      if (result.success) {
        setStats(result.data.summary);
        setOrders(result.data.orders);
        // Update pagination info from backend
        if (result.data.pagination) {
          setTotalCount(result.data.pagination.totalCount || result.data.orders.length);
        } else {
          setTotalCount(result.data.orders.length);
        }
      } else {
        console.error("Failed to fetch delivered stats:", result.error);
      }
    } catch (error) {
      console.error("Error fetching delivered stats:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, customDateRange, timePeriodGroupBy, currentPage, itemsPerPage, searchTerm, statusFilter, complicationFilter, sortBy, sortOrder, apiBaseUrl]);

  useEffect(() => {
    fetchDeliveredStats();
  }, [fetchDeliveredStats]);

  const syncTracking = async () => {
    try {
      setSyncing(true);

      const response = await fetch(
        `${apiBaseUrl}/api/shipping/acs/tracking/sync`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ limit: 200 }),
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log(
          `✅ Synced ${result.updated} of ${result.processed} orders`
        );
        // Refresh stats after sync
        await fetchDeliveredStats();
      } else {
        console.error("Failed to sync tracking:", result.error);
      }
    } catch (error) {
      console.error("Error syncing tracking:", error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchTrackingHistory = async (order: OrderWithTracking) => {
    if (!order.trackingId) {
      setTrackingError("No tracking information available");
      setIsLoadingTracking(false);
      return;
    }

    try {
      setIsLoadingTracking(true);
      setTrackingError(null);
      setTrackingData(null);
      
      // Since this is for ACS delivered stats, use 'acs' as the shipping company
      const shippingCompany = order.shippingCompany || "acs";
      
      console.log(`🔍 [TRACKING] Fetching tracking history for ${order.trackingId} from ${shippingCompany}`);

      const response = await fetch(
        `${apiBaseUrl}/api/shipping/${shippingCompany.toLowerCase()}/tracking/${order.trackingId}`
      );

      const result = await response.json();

      if (result.success) {
        console.log(`✅ [TRACKING] Successfully fetched tracking history`, result.data);
        setTrackingData(result.data);
        setTrackingError(null);
      } else {
        console.error("❌ [TRACKING] Failed:", result.error);
        // Handle error response with details
        const errorMessage = result.error || "Failed to fetch tracking history";
        const errorDetails = result.details || "";
        setTrackingError(errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage);
        setTrackingData(null);
      }
    } catch (error) {
      console.error("❌ [TRACKING] Error:", error);
      setTrackingError("Failed to fetch tracking history");
      setTrackingData(null);
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleViewTracking = (order: OrderWithTracking) => {
    console.log(`🔘 [TRACKING] Button clicked for order ${order.id}, trackingId: ${order.trackingId}`);
    setSelectedOrderForTracking(order);
    // Open modal immediately with loading state
    setShowTrackingModal(true);
    setTrackingData(null);
    setTrackingError(null);
    setIsLoadingTracking(true);
    // Fetch data while modal is open
    fetchTrackingHistory(order);
  };

  // Filter orders based on selected filters (status and complication only - time period filtering is done by backend)
  const filteredOrders = orders.filter((order) => {
    const trackingStatus = order.acsTrackingStatus?.status || "Transit";
    const isComplicated = order.acsTrackingStatus?.isComplicated || false;

    if (statusFilter !== "all" && trackingStatus !== statusFilter) {
      return false;
    }

    if (complicationFilter === "complicated" && !isComplicated) {
      return false;
    }

    if (complicationFilter === "simple" && isComplicated) {
      return false;
    }

    return true;
  });

  // Sort orders based on selected sort criteria
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (!sortBy) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case "id":
        aValue = a.id;
        bValue = b.id;
        break;
      case "trackingId":
        aValue = a.trackingId || "";
        bValue = b.trackingId || "";
        break;
      case "name":
        aValue = a.name || "";
        bValue = b.name || "";
        break;
      case "status":
        aValue = a.acsTrackingStatus?.status || "Transit";
        bValue = b.acsTrackingStatus?.status || "Transit";
        break;
      case "reason":
        aValue = renderReason(a.acsTrackingStatus?.reason);
        bValue = renderReason(b.acsTrackingStatus?.reason);
        break;
      case "lastChecked":
        const aLastChecked = parseDate(a.acsTrackingStatus?.lastCheckedAt);
        const bLastChecked = parseDate(b.acsTrackingStatus?.lastCheckedAt);
        aValue = aLastChecked ? aLastChecked.getTime() : 0;
        bValue = bLastChecked ? bLastChecked.getTime() : 0;
        break;
      case "orderDate":
        aValue = parseDate(a.time)?.getTime() || 0;
        bValue = parseDate(b.time)?.getTime() || 0;
        break;
      case "approveTime":
        aValue = getBestTimestamp(a) || 0;
        bValue = getBestTimestamp(b) || 0;
        break;
      case "shippingDate":
        const aShippingDate = getShippingDate(a);
        const bShippingDate = getShippingDate(b);
        aValue = aShippingDate ? new Date(aShippingDate).getTime() : 0;
        bValue = bShippingDate ? new Date(bShippingDate).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination logic - backend handles pagination, so orders are already paginated
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : Math.ceil(sortedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = sortedOrders; // Already paginated by backend

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Delivered":
        return "bg-green-100 text-green-800";
      case "Returned":
        return "bg-red-100 text-red-800";
      case "Transit":
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Delivered":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "Returned":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "Transit":
      default:
        return <Truck className="w-4 h-4 text-blue-600" />;
    }
  };

  // ACS reason code to human-friendly text mapping (does not change status)
  const ACS_REASON_MAP: Record<string, string> = {
    AD1: "ORDER FOR OFFICE PICKUP - STATUS 5",
    AD3: "FORCE MAJEURE - STATUS 5",
    AD8: "Pick-up from ACS Shop / Sender's Request - STATUS 5",
    AP1: "Delivery Refusal (due to charge) - STATUS 1",
    AP2: "UNABLE TO PAY - STATUS 1",
    AP3: "NOT ACCEPTABLE SHIPMENT - STATUS 1",
    AP4: "DECEASED - STATUS 1",
    AS1: "ABSENT - STATUS 3",
    DP1: "DIFFICULT TO ACCESS AREA - STATUS 5",
    LS1: "UNKNOWN CONSIGNEE - STATUS 2",
    LS2: "Shipment is on transit to destination – STATUS 2",
    LS3: "ERROR OR INADEQUATE ADDRESS - STATUS 2",
    PA1: "NEW DELIVERY DATE ORDER OF SHIPPER - STATUS 5",
    PA2: "NEW DELIVERY DATE ORDER OF CONSIGNEE - STATUS 5",
    PA4: "Reschedule / Redirect - STATUS 5",
  };

  const renderReason = (rawReason?: string | null) => {
    if (!rawReason) return "-";
    // If backend provides only the code, map directly
    const trimmed = rawReason.trim();
    if (ACS_REASON_MAP[trimmed])
      return `${trimmed} - ${ACS_REASON_MAP[trimmed]}`;
    // If backend provides code embedded in text, try to extract known code prefix
    const codeMatch = trimmed.match(
      /^(AD1|AD3|AD8|AP1|AP2|AP3|AP4|AS1|DP1|LS1|LS2|LS3|PA1|PA2|PA4)\b/
    );
    if (codeMatch && ACS_REASON_MAP[codeMatch[1]]) {
      return `${trimmed} - ${ACS_REASON_MAP[codeMatch[1]]}`;
    }
    // Otherwise, show raw reason text
    return trimmed;
  };

  // Export functions
  const exportToCSV = () => {
    const headers = [
      "#",
      "Order ID", 
      "Tracking ID",
      "Customer",
      "Status",
      "Reason",
      "Last Checked",
      "Order Date",
      "APPROVE TIME",
      "Shipping Date"
    ];

    const csvContent = [
      headers.join(","),
      ...sortedOrders.map((order, index) => {
        const trackingStatus = order.acsTrackingStatus;
        const status = trackingStatus?.status || "Transit";
        
        return [
          startIndex + index + 1,
          order.id,
          order.trackingId || "",
          `"${(order.name || "N/A").replace(/"/g, '""')}"`,
          status,
          `"${renderReason(trackingStatus?.reason).replace(/"/g, '""')}"`,
          trackingStatus?.lastCheckedAt ? (() => {
            const date = parseDate(trackingStatus.lastCheckedAt);
            return date ? `"${date.toLocaleDateString()}\n${date.toLocaleTimeString()}"` : "-";
          })() : "-",
          (() => {
            const date = parseDate(order.time);
            return date ? `"${date.toLocaleDateString()}\n${date.toLocaleTimeString()}"` : "Invalid date";
          })(),
          getBestTimestamp(order) ? (() => {
            const date = new Date(getBestTimestamp(order)!);
            return `"${date.toLocaleDateString()}\n${date.toLocaleTimeString()}"`;
          })() : "Not approved",
          getShippingDate(order) ? (() => {
            const date = new Date(getShippingDate(order)!);
            return `"${date.toLocaleDateString()}\n${date.toLocaleTimeString()}"`;
          })() : "Not shipped"
        ].join(",");
      })
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `delivered-statistics-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToExcel = () => {
    // For Excel export, we'll create a CSV with .xlsx extension
    // In a real implementation, you might want to use a library like xlsx
    const headers = [
      "#",
      "Order ID", 
      "Tracking ID",
      "Customer",
      "Status",
      "Reason",
      "Last Checked",
      "Order Date",
      "APPROVE TIME",
      "Shipping Date"
    ];

    const csvContent = [
      headers.join("\t"),
      ...sortedOrders.map((order, index) => {
        const trackingStatus = order.acsTrackingStatus;
        const status = trackingStatus?.status || "Transit";
        
        return [
          startIndex + index + 1,
          order.id,
          order.trackingId || "",
          order.name || "N/A",
          status,
          renderReason(trackingStatus?.reason),
          trackingStatus?.lastCheckedAt ? (() => {
            const date = parseDate(trackingStatus.lastCheckedAt);
            return date ? `${date.toLocaleDateString()}\n${date.toLocaleTimeString()}` : "-";
          })() : "-",
          (() => {
            const date = parseDate(order.time);
            return date ? `${date.toLocaleDateString()}\n${date.toLocaleTimeString()}` : "Invalid date";
          })(),
          getBestTimestamp(order) ? (() => {
            const date = new Date(getBestTimestamp(order)!);
            return `${date.toLocaleDateString()}\n${date.toLocaleTimeString()}`;
          })() : "Not approved",
          getShippingDate(order) ? (() => {
            const date = new Date(getShippingDate(order)!);
            return `${date.toLocaleDateString()}\n${date.toLocaleTimeString()}`;
          })() : "Not shipped"
        ].join("\t");
      })
    ].join("\n");

    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `delivered-statistics-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
  };

  const handleExport = async (exportType: "csv" | "excel") => {
    setIsExporting(true);
    try {
      if (exportType === "csv") {
        exportToCSV();
      } else {
        exportToExcel();
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: "#264a3e" }}
        ></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col space-y-4">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              ACS Delivery Statistics
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Real-time tracking status for all ACS shipments
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-3">
            {/* Date Range Selector */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setShowCustomDateRange(e.target.value === "custom");
                  setTimePeriodGroupBy("shippingDate"); // Reset to shipping date when changing date range
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] w-full sm:w-auto"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Time Period By Selector */}
            {dateRange !== "all" && (
              <div className="flex items-center space-x-2 w-full sm:w-auto">
                <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">by</span>
                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <select
                  value={timePeriodGroupBy}
                  onChange={(e) => {
                    setTimePeriodGroupBy(e.target.value);
                    // Refresh orders when time period selection changes
                    fetchDeliveredStats();
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] w-full sm:w-auto"
                >
                  <option value="orderDate">Order Date</option>
                  <option value="approveTime">Approve Time</option>
                  <option value="shippingDate">Shipping Date</option>
                  <option value="lastChecked">Last Checked</option>
                </select>
              </div>
            )}

            {showCustomDateRange && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({
                      ...prev,
                      start: e.target.value,
                    }))
                  }
                  className="border border-gray-300 rounded-md px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] w-full sm:w-auto"
                />
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({
                      ...prev,
                      end: e.target.value,
                    }))
                  }
                  className="border border-gray-300 rounded-md px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] w-full sm:w-auto"
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-3 w-full sm:w-auto">
              {/* Sync Button */}
              <button
                onClick={syncTracking}
                disabled={syncing}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}
                />
                {syncing ? "Syncing..." : "Sync Tracking"}
              </button>

              {/* Export Button */}
              <button
                onClick={() => setShowExportModal(true)}
                disabled={orders.length === 0}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>

              {/* Refresh Button */}
              <button
                onClick={fetchDeliveredStats}
                disabled={loading}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards - Horizontal Scroll on Mobile */}
      <div className="lg:hidden overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex space-x-3 min-w-max">
          {/* Total Shipping Orders */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Total Shipping Orders</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.Transit + stats.Delivered + stats.Returned}
                </p>
              </div>
            </div>
          </div>

          {/* Transit */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">In Transit</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.Transit}
                </p>
              </div>
            </div>
          </div>

          {/* Delivered */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Delivered</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.Delivered}
                </p>
              </div>
            </div>
          </div>

          {/* Returned */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Returned</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.Returned}
                </p>
              </div>
            </div>
          </div>

          {/* Complicated */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Complicated</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.Complicated}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Statistics Cards */}
      <div className="hidden lg:grid grid-cols-5 gap-3 sm:gap-4">
        {/* Total Shipping Orders */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Package className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: "#264a3e" }} />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Shipping Orders</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {stats.Transit + stats.Delivered + stats.Returned}
              </p>
            </div>
          </div>
        </div>

        {/* Transit */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">In Transit</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {stats.Transit}
              </p>
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Delivered</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {stats.Delivered}
              </p>
            </div>
          </div>
        </div>

        {/* Returned */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Returned</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {stats.Returned}
              </p>
            </div>
          </div>
        </div>

        {/* Complicated (only non-delivered) */}
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 col-span-2 md:col-span-1">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Complicated</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {stats.Complicated}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
          {/* Mobile: Filter icon + Search Input + Search Button in one row */}
          <div className="flex items-center space-x-2 sm:hidden flex-1">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  // When user clears search, automatically search with empty string
                  if (e.target.value === "") {
                    setSearchTerm("");
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    setSearchTerm(searchInput);
                  }
                }}
                className="block w-full pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-[#264a3e] focus:border-[#264a3e] text-xs"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setSearchTerm("");
                  }}
                  className="absolute inset-y-0 right-10 pr-2 flex items-center text-gray-400 hover:text-gray-600 z-10"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setSearchTerm(searchInput)}
              className="flex-shrink-0 text-white p-2 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e]"
              style={{ backgroundColor: "#264a3e" }}
              title="Search"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>

          {/* Desktop: Filter label + Search Input with button inside */}
          <div className="hidden sm:flex items-center space-x-2 flex-shrink-0">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* Desktop Search Input */}
          <div className="hidden sm:flex items-center space-x-2 flex-1 sm:max-w-md">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by Order ID, Tracking ID, Phone, or Customer Name..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  // When user clears search, automatically search with empty string
                  if (e.target.value === "") {
                    setSearchTerm("");
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    setSearchTerm(searchInput);
                  }
                }}
                className="block w-full pl-10 pr-24 py-2 border border-gray-300 rounded-md focus:ring-[#264a3e] focus:border-[#264a3e] text-xs sm:text-sm"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setSearchTerm("");
                  }}
                  className="absolute inset-y-0 right-12 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-10"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {/* Search Button - Icon only, on right side of input */}
              <button
                onClick={() => setSearchTerm(searchInput)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-1.5 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e] z-10"
                style={{ backgroundColor: "#264a3e" }}
                title="Search"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Status and Complication Filters */}
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] flex-1 sm:flex-initial"
            >
              <option value="all">All Statuses</option>
              <option value="Transit">In Transit</option>
              <option value="Delivered">Delivered</option>
              <option value="Returned">Returned</option>
            </select>

            {/* Complication Filter */}
            <select
              value={complicationFilter}
              onChange={(e) => setComplicationFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] flex-1 sm:flex-initial"
            >
              <option value="all">All Orders</option>
              <option value="complicated">Complicated Only</option>
              <option value="simple">Simple Only</option>
            </select>
          </div>

          {/* <div className="text-sm text-gray-600">
            {sortedOrders.length} of {totalCount > 0 ? totalCount : orders.length} orders (Page {currentPage} of {totalPages})
          </div> */}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden w-full">
        <div className="overflow-x-auto w-full sm:mx-0">
          <table className="w-full divide-y divide-gray-200 min-w-max">
            <thead className="bg-gray-50">
              <tr>
            <th className="pl-4 pr-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              #
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              <button
                onClick={() => {
                  if (sortBy === "id") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("id");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Order ID</span>
                {sortBy === "id" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
              <button
                onClick={() => {
                  if (sortBy === "trackingId") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("trackingId");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Tracking ID</span>
                {sortBy === "trackingId" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              <button
                onClick={() => {
                  if (sortBy === "name") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("name");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Customer</span>
                {sortBy === "name" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              <button
                onClick={() => {
                  if (sortBy === "status") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("status");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Status</span>
                {sortBy === "status" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell" style={{ minWidth: "250px" }}>
              <button
                onClick={() => {
                  if (sortBy === "reason") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("reason");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Reason</span>
                {sortBy === "reason" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">
              <button
                onClick={() => {
                  if (sortBy === "lastChecked") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("lastChecked");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Last Checked</span>
                {sortBy === "lastChecked" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
              <button
                onClick={() => {
                  if (sortBy === "orderDate") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("orderDate");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Order Date</span>
                {sortBy === "orderDate" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">
              <button
                onClick={() => {
                  if (sortBy === "approveTime") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("approveTime");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>APPROVE TIME</span>
                {sortBy === "approveTime" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">
              <button
                onClick={() => {
                  if (sortBy === "shippingDate") {
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy("shippingDate");
                    setSortOrder("desc");
                  }
                }}
                className="flex items-center space-x-1 hover:text-gray-700"
              >
                <span>Shipping Date</span>
                {sortBy === "shippingDate" && (
                  <span className="text-[#264a3e]">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
              Account
            </th>
            <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              Actions
            </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No orders found</p>
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order, index) => {
                  const trackingStatus = order.acsTrackingStatus;
                  const status = trackingStatus?.status || "Transit";
                  const isComplicated = trackingStatus?.isComplicated || false;
                  // Only mark as orange if complicated AND not delivered
                  const showOrangeBg = isComplicated && status !== "Delivered";

                  return (
                    <tr
                      key={order.id}
                      className={
                        showOrangeBg ? "bg-orange-50" : "hover:bg-gray-50"
                      }
                    >
                      <td className="pl-4 pr-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        #{startIndex + index + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        #{order.id}
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell">
                        {order.trackingId}
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm text-gray-900">
                          {order.name || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.phone || "-"}
                        </div>
                        {/* Show tracking ID on mobile */}
                        <div className="text-xs text-blue-600 mt-1 md:hidden">
                          {order.trackingId}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(status)}
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              status
                            )}`}
                          >
                            {status}
                          </span>
                          {isComplicated && (
                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-xs sm:text-sm text-gray-900 hidden lg:table-cell" style={{ minWidth: "250px", maxWidth: "400px" }}>
                        {status === "Delivered" && isComplicated ? (
                          <div className="break-words">
                            <div className="font-bold italic text-gray-900">
                              Delivered, but with complication:
                            </div>
                            <div className="text-gray-600 mt-1">
                              {renderReason(trackingStatus?.reason)}
                            </div>
                          </div>
                        ) : status === "Delivered" ? (
                          <div className="break-words text-gray-400">
                            -
                          </div>
                        ) : (
                          <div className="break-words">
                            {renderReason(trackingStatus?.reason)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden xl:table-cell">
                        {trackingStatus?.lastCheckedAt ? (
                          (() => {
                            const date = parseDate(trackingStatus.lastCheckedAt);
                            return date ? (
                              <div className="flex items-center">
                                <Clock className="w-3 h-3 mr-1 text-gray-400" />
                                <div>
                                  <div>{date.toLocaleDateString()}</div>
                                  <div className="text-xs text-gray-400">{date.toLocaleTimeString()}</div>
                                </div>
                              </div>
                            ) : "-";
                          })()
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                        <div>
                          {(() => {
                            // Use parseDate to handle the timestamp properly
                            const date = parseDate(order.time);
                            return date ? (
                              <>
                                <div>{date.toLocaleDateString()}</div>
                                <div className="text-xs text-gray-400">{date.toLocaleTimeString()}</div>
                              </>
                            ) : (
                              <span className="text-gray-400 italic">Invalid date</span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden xl:table-cell">
                        {getBestTimestamp(order) ? (
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                            <div>
                              <div>{new Date(getBestTimestamp(order)!).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-400">{new Date(getBestTimestamp(order)!).toLocaleTimeString()}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not approved</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden xl:table-cell">
                        {getShippingDate(order) ? (
                          <div className="flex items-center">
                            <Package className="w-3 h-3 mr-1 text-gray-400" />
                            <div>
                              <div>{new Date(getShippingDate(order)!).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-400">{new Date(getShippingDate(order)!).toLocaleTimeString()}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not shipped</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap hidden lg:table-cell">
                        {(() => {
                          // For manual orders without user, show creator
                          if ((!order.user || order.user === null) && order.source === "manual" && order.createdBy) {
                            return (
                              <div className="flex items-center">
                                <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 break-words">
                                    {order.createdBy.username}
                                  </div>
                                  <div className="text-xs text-gray-500">{order.createdBy.role}</div>
                                </div>
                              </div>
                            );
                          }
                          
                          // If no user ID, show N/A
                          if (!order.user) {
                            return (
                              <div className="flex items-center">
                                <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                                <div className="text-sm text-gray-500">
                                  N/A
                                </div>
                              </div>
                            );
                          }
                          
                          // AlterCPA removed for Greek-CC
                          // AlterCPA removed for Greek-CC
                          const account = null as any; // getAccountById(altercpaState, order.user);
                          return account ? (
                            <div className="flex items-center">
                              <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900 break-words">
                                  {account.username}
                                </div>
                                <div className="text-xs text-gray-500">{account.level}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                              <div className="text-sm text-gray-500">
                                Account ID: {order.user || "N/A"}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {order.trackingId && (
                          <button
                            onClick={() => handleViewTracking(order)}
                            className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation"
                            style={{ backgroundColor: "#264a3e" }}
                            title="View Tracking History"
                          >
                            <History className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white px-3 sm:px-4 lg:px-6 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(startIndex + itemsPerPage, totalCount > 0 ? totalCount : sortedOrders.length)}
                </span>{" "}
                of <span className="font-medium">{totalCount > 0 ? totalCount : sortedOrders.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === currentPage
                          ? "z-10 bg-[#264a3e] border-[#264a3e] text-white"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Export Data
                </h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Choose the export format for your data:
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Exporting {totalCount > 0 ? totalCount : sortedOrders.length} orders with all columns
                </p>

                <div className="space-y-3">
                  {/* CSV Export Option */}
                  <button
                    onClick={() => handleExport("csv")}
                    disabled={isExporting}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center">
                      <FileText className="w-6 h-6 text-green-600 mr-3" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">CSV Format</div>
                        <div className="text-xs text-gray-500">Comma-separated values</div>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-gray-400" />
                  </button>

                  {/* Excel Export Option */}
                  <button
                    onClick={() => handleExport("excel")}
                    disabled={isExporting}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center">
                      <FileSpreadsheet className="w-6 h-6 text-green-600 mr-3" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">Excel Format</div>
                        <div className="text-xs text-gray-500">Microsoft Excel (.xlsx)</div>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowExportModal(false)}
                  disabled={isExporting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>

              {/* Loading State */}
              {isExporting && (
                <div className="mt-4 flex items-center justify-center">
                  <RefreshCw className="w-4 h-4 animate-spin text-green-600 mr-2" />
                  <span className="text-sm text-gray-600">Exporting...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      <TrackingModal
        isOpen={showTrackingModal}
        onClose={() => {
          setShowTrackingModal(false);
          setTrackingData(null);
          setTrackingError(null);
        }}
        trackingData={trackingData}
        shippingCompany={selectedOrderForTracking?.shippingCompany || "acs"}
        loading={isLoadingTracking}
        error={trackingError}
      />
    </div>
  );
};

export default DeliveredStatistics;
